// Single source of truth for which routes are "marketing surfaces" -
// pages that render their own Tesla MarketingNav and must therefore
// suppress the portal TopBar + DeliveryBanner (PortalChromeGate reads
// this). Extracted from PortalChromeGate so the E-008 guard test can
// import the exact same logic without pulling in the "use client"
// component.
//
// E-008 (dual-nav bug, recurring): when a public page renders
// MarketingNav but its path is NOT recognized here, the portal TopBar
// stacks on top and the visitor sees two navs. The guard test in
// tests/unit/marketing/marketing-chrome-guard.test.ts enumerates every
// page under app/ that renders MarketingNav and fails if any is not
// covered by isMarketingPath - so a new public page can never
// reintroduce the bug silently.

import { stripEsPrefix } from "@/lib/i18n/es-path";

export const EXACT_MARKETING_PATHS: ReadonlySet<string> = new Set([
  "/",
  "/about",
  "/about/ecuadorian-rose-farms",
  "/about/ordering",
  "/about/packing",
  "/catalog",
  "/sign-in",
  "/contact",
  "/privacy",
  "/terms",
  "/faq",
  // Public signup flow (marketing surface for signed-out applicants).
  "/sign-up",
  "/sign-up/confirm",
  "/sign-up/success",
  // PASTE #229/#230 Wave 3: the weddings trade cluster - landing +
  // sourcing guide + stem-to-box calculator. Each renders MarketingNav;
  // registering them here suppresses the portal TopBar (E-008 fix).
  "/weddings",
  "/resources/wedding-flowers",
  "/resources/wedding-calculator",
  // PASTE #236 Wave 3: events + subscriptions occasion landings. Each
  // renders MarketingNav; register so the portal TopBar is suppressed
  // (E-008). /es variants are covered by stripEsPrefix in isMarketingPath.
  "/events",
  "/subscriptions",
  // PASTE #237 Wave 3: /how-it-works flagship (renders MarketingNav).
  "/how-it-works",
  // MKT-PASTE #1 Part B: Ecuador cornerstone (renders MarketingNav).
  "/ecuadorian-roses-wholesale",
  // MKT-PASTE #8/#9: metro landing pages (Florida takeover).
  "/wholesale-flowers-tampa",
  "/wholesale-flowers-west-palm-beach",
  // MKT-#61: Boca Raton metro.
  "/wholesale-flowers-boca-raton",
  // MKT-#64: Naples - Fort Myers corridor metro.
  "/wholesale-flowers-naples-fort-myers",
  "/wholesale-flowers-orlando",
  // MKT-#88/#89: Fort Lauderdale + Miami/Doral (bilingual).
  "/wholesale-flowers-fort-lauderdale",
  "/wholesale-flowers-miami",
  // MKT-#40: Florida state hub (parent of the city pages).
  "/wholesale-flowers-florida",
]);

export function isMarketingPath(pathname: string): boolean {
  // PASTE #233: /es/<route> Spanish variants share the same chrome as
  // their bare EN route. Strip the /es prefix before matching so every
  // public route's Spanish twin renders single-nav (E-008 guard).
  const p = stripEsPrefix(pathname);
  if (EXACT_MARKETING_PATHS.has(p)) return true;
  // Variety detail + category landing routes under /catalog/.
  if (p.startsWith("/catalog/")) return true;
  return false;
}
